/**
 * Text-to-Settings Sync Layer
 * Parses user description (Arabic + English) and returns UI setting updates.
 * Does NOT modify any existing system — just produces a settings patch.
 */

import type { AspectRatio } from './engineTypes';

export interface SettingsPatch {
  aspectRatio?: AspectRatio;
  camera?: Record<string, string>;
  light?: Record<string, string>;
  character?: Record<string, string>;
  global?: Record<string, string | number | boolean>;
}

// ── Aspect Ratio Detection ──
const ASPECT_RULES: { pattern: RegExp; value: AspectRatio }[] = [
  { pattern: /\b(مربع|square|1:\s*1)\b/i, value: '1:1' },
  { pattern: /\b(بورتريه|portrait|4:\s*5|عمودي)\b/i, value: '4:5' },
  { pattern: /\b(ستوري|story|9:\s*16|ريلز|reels?)\b/i, value: '9:16' },
  { pattern: /\b(وايد\s*سكرين|widescreen|16:\s*9|سينمائي عريض)\b/i, value: '16:9' },
  { pattern: /\b(الترا\s*وايد|ultra.?wide|21:\s*9|سينما\s*سكوب|cinemascope)\b/i, value: '21:9' },
  { pattern: /\b(anamorphic|أنامورفيك|2\.39:\s*1)\b/i, value: '2.39:1' },
  { pattern: /\b(3:\s*2|كلاسيك\s*فوتو|classic photo)\b/i, value: '3:2' },
  { pattern: /\b(2:\s*3|فل بورتريه|full portrait)\b/i, value: '2:3' },
  { pattern: /\b(4:\s*3|تلفزيون|tv)\b/i, value: '4:3' },
];

// ── Camera Settings Detection ──
interface MatchRule {
  pattern: RegExp;
  key: string;
  value: string;
}

const CAMERA_RULES: MatchRule[] = [
  // Lens
  { pattern: /\b(8\s*mm|فيش\s*آي|fisheye)\b/i, key: 'lens', value: '8mm' },
  { pattern: /\b14\s*mm\b/i, key: 'lens', value: '14mm' },
  { pattern: /\b(16\s*mm)\b/i, key: 'lens', value: '16mm' },
  { pattern: /\b(20\s*mm)\b/i, key: 'lens', value: '20mm' },
  { pattern: /\b(24\s*mm|wide angle|وايد انجل|زاوية واسعة)\b/i, key: 'lens', value: '24mm' },
  { pattern: /\b(28\s*mm)\b/i, key: 'lens', value: '28mm' },
  { pattern: /\b(35\s*mm)\b/i, key: 'lens', value: '35mm' },
  { pattern: /\b(50\s*mm|نيفتي فيفتي|nifty fifty)\b/i, key: 'lens', value: '50mm' },
  { pattern: /\b(85\s*mm|بورتريه لنس|portrait lens)\b/i, key: 'lens', value: '85mm' },
  { pattern: /\b(105\s*mm)\b/i, key: 'lens', value: '105mm' },
  { pattern: /\b(135\s*mm)\b/i, key: 'lens', value: '135mm' },
  { pattern: /\b(200\s*mm|تيلي|telephoto)\b/i, key: 'lens', value: '200mm' },
  { pattern: /\b(300\s*mm)\b/i, key: 'lens', value: '300mm' },
  { pattern: /\b(ماكرو|macro)\b/i, key: 'lens', value: '100mm_macro' },
  { pattern: /\b(تيلت\s*شيفت|tilt.?shift)\b/i, key: 'lens', value: 'tilt_shift' },
  { pattern: /\b(أنامورفيك|anamorphic)\b/i, key: 'lens', value: 'anamorphic' },

  // Shot Type
  { pattern: /\b(اكستريم كلوز اب|extreme close.?up|ECU)\b/i, key: 'shotType', value: 'extreme_close' },
  { pattern: /\b(كلوز اب|close.?up|CU|قريب|لقطة قريبة)\b/i, key: 'shotType', value: 'close_up' },
  { pattern: /\b(ميديم شوت|medium shot|MS|لقطة متوسطة)\b/i, key: 'shotType', value: 'medium' },
  { pattern: /\b(وايد شوت|wide shot|WS|لقطة واسعة|لقطة عامة)\b/i, key: 'shotType', value: 'wide' },
  { pattern: /\b(اكستريم وايد|extreme wide|establishing|لقطة تأسيسية)\b/i, key: 'shotType', value: 'extreme_wide' },
  { pattern: /\b(فل بودي|full body|جسم كامل)\b/i, key: 'shotType', value: 'full_body' },
  { pattern: /\b(كاوبوي|cowboy|american shot)\b/i, key: 'shotType', value: 'cowboy' },
  { pattern: /\b(بانوراما|panoramic|بانورامي)\b/i, key: 'shotType', value: 'panoramic' },
  { pattern: /\b(dutch angle|زاوية مائلة)\b/i, key: 'shotType', value: 'dutch_angle' },
  { pattern: /\b(over.?the.?shoulder|أوفر شولدر)\b/i, key: 'shotType', value: 'over_shoulder' },

  // Perspective / Angle
  { pattern: /\b(هاي انجل|high angle|من فوق|زاوية عالية)\b/i, key: 'perspective', value: 'high_angle' },
  { pattern: /\b(لو انجل|low angle|من تحت|زاوية منخفضة)\b/i, key: 'perspective', value: 'low_angle' },
  { pattern: /\b(بيردز اي|bird'?s?.?eye|top.?down|من أعلى)\b/i, key: 'perspective', value: 'birds_eye' },
  { pattern: /\b(وورمز اي|worm'?s?.?eye|من الأرض)\b/i, key: 'perspective', value: 'worms_eye' },
  { pattern: /\b(اي ليفل|eye level|مستوى العين)\b/i, key: 'perspective', value: 'eye_level' },
  { pattern: /\b(بروفايل|profile|جانبي|سايد فيو|side view)\b/i, key: 'perspective', value: 'profile' },
  { pattern: /\b(POV|first person|من منظور)\b/i, key: 'perspective', value: 'pov' },
  { pattern: /\b(ايريال|aerial|drone|درون)\b/i, key: 'perspective', value: 'aerial' },
  { pattern: /\b(كرين|crane)\b/i, key: 'perspective', value: 'crane' },

  // DOF
  { pattern: /\b(شالو|shallow|خلفية ضبابية|بوكيه|bokeh)\b/i, key: 'dof', value: 'shallow' },
  { pattern: /\b(ديب فوكس|deep focus|كل حاجة واضحة|everything sharp)\b/i, key: 'dof', value: 'deep' },
  { pattern: /\b(very shallow|فيري شالو|dreamy)\b/i, key: 'dof', value: 'very_shallow' },

  // Camera Movement
  { pattern: /\b(دولي|dolly in|push in)\b/i, key: 'cameraMovement', value: 'dolly_in' },
  { pattern: /\b(اوربت|orbit)\b/i, key: 'cameraMovement', value: 'orbit' },
  { pattern: /\b(تراكينج|tracking|trucking)\b/i, key: 'cameraMovement', value: 'tracking' },
  { pattern: /\b(ستاتيك|static|ثابت)\b/i, key: 'cameraMovement', value: 'static' },
  { pattern: /\b(هاند هيلد|handheld|يدوي)\b/i, key: 'cameraMovement', value: 'handheld_shake' },
  { pattern: /\b(سلو بوش|slow push)\b/i, key: 'cameraMovement', value: 'slow_push' },
];

// ── Lighting Detection ──
const LIGHT_RULES: MatchRule[] = [
  // Light Style
  { pattern: /\b(ريمبراندت|rembrandt)\b/i, key: 'lightStyle', value: 'rembrandt' },
  { pattern: /\b(باترفلاي|butterfly|paramount)\b/i, key: 'lightStyle', value: 'butterfly' },
  { pattern: /\b(سبليت|split light)\b/i, key: 'lightStyle', value: 'split' },
  { pattern: /\b(ريم لايت|rim light|إضاءة حواف)\b/i, key: 'lightStyle', value: 'rim' },
  { pattern: /\b(باك لايت|backlight|إضاءة خلفية)\b/i, key: 'lightStyle', value: 'backlight' },
  { pattern: /\b(فوليوميتريك|volumetric|god rays|أشعة)\b/i, key: 'lightStyle', value: 'volumetric' },
  { pattern: /\b(نيون|neon)\b/i, key: 'lightStyle', value: 'neon' },
  { pattern: /\b(سوفت بوكس|softbox)\b/i, key: 'lightStyle', value: 'softbox' },
  { pattern: /\b(لو كي|low key|إضاءة منخفضة|درامي|dramatic)\b/i, key: 'lightStyle', value: 'low_key' },
  { pattern: /\b(هاي كي|high key|إضاءة عالية|مشرق)\b/i, key: 'lightStyle', value: 'high_key' },
  { pattern: /\b(كياروسكورو|chiaroscuro)\b/i, key: 'lightStyle', value: 'chiaroscuro' },
  { pattern: /\b(سيلويت|silhouette|ظل)\b/i, key: 'lightStyle', value: 'silhouette' },
  { pattern: /\b(شمعة|candlelight|شموع)\b/i, key: 'lightStyle', value: 'candlelight' },
  { pattern: /\b(قمر|moonlight|ضوء القمر)\b/i, key: 'lightStyle', value: 'moonlight' },
  { pattern: /\b(نار|fire|bonfire|حريق)\b/i, key: 'lightStyle', value: 'fire' },
  { pattern: /\b(رينج لايت|ring light)\b/i, key: 'lightStyle', value: 'ring_light' },
  { pattern: /\b(طبيعي|natural|إضاءة طبيعية|natural light)\b/i, key: 'lightStyle', value: 'natural' },
  { pattern: /\b(نوار|noir)\b/i, key: 'lightStyle', value: 'dramatic' },

  // Light Preset
  { pattern: /\b(جولدن اور|golden hour|ساعة ذهبية|غروب)\b/i, key: 'lightPreset', value: 'golden_hour' },
  { pattern: /\b(بلو اور|blue hour|ساعة زرقاء)\b/i, key: 'lightPreset', value: 'blue_hour' },
  { pattern: /\b(سايبربانك|cyberpunk)\b/i, key: 'lightPreset', value: 'cyberpunk' },
  { pattern: /\b(نوار|noir|مودي)\b/i, key: 'lightPreset', value: 'moody_noir' },
  { pattern: /\b(ضوء نهار|daylight|نهاري)\b/i, key: 'lightPreset', value: 'natural_daylight' },
  { pattern: /\b(رعب|horror|مخيف|eerie)\b/i, key: 'lightPreset', value: 'horror' },
  { pattern: /\b(رومانسي|romantic|رومانتيك)\b/i, key: 'lightPreset', value: 'romantic' },
  { pattern: /\b(ساي فاي|sci.?fi|خيال علمي)\b/i, key: 'lightPreset', value: 'sci_fi_cold' },
  { pattern: /\b(ستوديو|studio beauty)\b/i, key: 'lightPreset', value: 'studio_beauty' },
  { pattern: /\b(غائم|overcast|ملبد)\b/i, key: 'lightPreset', value: 'overcast_soft' },
];

// ── Global Settings Detection ──
interface GlobalRule {
  pattern: RegExp;
  key: string;
  value: string | number | boolean;
}

const GLOBAL_RULES: GlobalRule[] = [
  // Sharpness
  { pattern: /\b(شارب|sharp|حاد|sharpness|وضوح عالي)\b/i, key: 'sharpness', value: 85 },
  { pattern: /\b(سوفت|soft|ناعم|dreamy)\b/i, key: 'sharpness', value: 25 },

  // Film Grain
  { pattern: /\b(فيلم جرين|film grain|حبيبات|grainy)\b/i, key: 'filmGrain', value: true },

  // Render Style
  { pattern: /\b(فوتوريالستيك|photorealistic|واقعي|realistic)\b/i, key: 'renderStyle', value: 'photorealistic' },
  { pattern: /\b(هايبر ريل|hyperreal|ultra detail)\b/i, key: 'renderStyle', value: 'hyperreal' },
  { pattern: /\b(أنمي|anime|كارتون|cartoon|cel.?shaded|تون)\b/i, key: 'renderStyle', value: 'cel_shaded' },
  { pattern: /\b(بينترلي|painterly|زيتي|oil paint)\b/i, key: 'renderStyle', value: 'painterly' },
  { pattern: /\b(جيم انجين|game engine|unreal|يونريال)\b/i, key: 'renderStyle', value: 'game_engine' },

  // Composition
  { pattern: /\b(ثلثين|rule of thirds|قاعدة الأثلاث)\b/i, key: 'ruleOfThirds', value: true },
  { pattern: /\b(جولدن ريشيو|golden ratio|النسبة الذهبية)\b/i, key: 'goldenRatio', value: true },
  { pattern: /\b(سيميتري|symmetry|تماثل|متماثل)\b/i, key: 'symmetryMode', value: true },
  { pattern: /\b(سنتر|centered|وسط|مركز)\b/i, key: 'centeredCinematic', value: true },

  // VFX
  { pattern: /\b(ضباب|fog|haze|شبورة)\b/i, key: 'fogHaze', value: 55 },
  { pattern: /\b(دخان|smoke|تدخين)\b/i, key: 'cinematicSmoke', value: 50 },
  { pattern: /\b(تراب|dust|غبار)\b/i, key: 'dustParticles', value: 45 },
  { pattern: /\b(بلوم|bloom|glow|توهج)\b/i, key: 'bloomGlow', value: 50 },
  { pattern: /\b(مطر|rain|أمطار)\b/i, key: 'particles', value: 'rain' },
  { pattern: /\b(ثلج|snow)\b/i, key: 'particles', value: 'snow' },
  { pattern: /\b(شرار|sparks|إمبرز|embers)\b/i, key: 'particles', value: 'embers' },

  // Color Grading
  { pattern: /\b(تيل اند اورانج|teal.?(?:and|&)?.?orange)\b/i, key: 'tealOrangePreset', value: true },
  { pattern: /\b(فينتيج|vintage|retro|ريترو|قديم)\b/i, key: 'vintageMode', value: true },
  { pattern: /\b(سيبيا|sepia)\b/i, key: 'lutPreset', value: 'sepia' },
  { pattern: /\b(بلاك اند وايت|black.?(?:and|&)?.?white|أبيض وأسود|ابيض واسود)\b/i, key: 'lutPreset', value: 'noir' },

  // Skin/Beauty
  { pattern: /\b(strong\s+skin\s+retouch|very\s+high\s+skin\s+retouch|ريتاتش\s*قوي|تنقيح\s*قوي)\b/i, key: 'skinSmoothing', value: 85 },
  { pattern: /\b(skin\s+retouch|retouch\s+skin|ريتاتش\s*بشرة|تنقيح\s*بشرة)\b/i, key: 'skinSmoothing', value: 65 },
  { pattern: /\b(سموث|smooth skin|بشرة ناعمة|تنعيم)\b/i, key: 'skinSmoothing', value: 60 },
  { pattern: /\b(ريتاتش|retouch|تنقيح)\b/i, key: 'skinSmoothing', value: 45 },
];

// ── Scene Mood → Auto Settings (when no explicit settings given) ──
interface MoodProfile {
  pattern: RegExp;
  camera: Record<string, string>;
  light: Record<string, string>;
  global: Record<string, string | number | boolean>;
}

const MOOD_PROFILES: MoodProfile[] = [
  {
    pattern: /\b(مخيف|رعب|horror|scary|eerie|مرعب|كابوس|nightmare|dark room|غرفة مظلمة|شرير|sinister)\b/i,
    camera: { shotType: 'close_up', perspective: 'low_angle', lens: '35mm', dof: 'shallow' },
    light: { lightStyle: 'low_key', lightPreset: 'horror', intensity: '35' },
    global: { fogHaze: 40, cinematicSmoke: 30, contrastCurve: 75, shadowBalance: 25, saturationIntensity: 30 },
  },
  {
    pattern: /\b(رومانسي|romantic|حب|love|عشق|لطيف|cute|حنون|tender)\b/i,
    camera: { shotType: 'medium', perspective: 'eye_level', lens: '85mm', dof: 'shallow' },
    light: { lightStyle: 'natural', lightPreset: 'golden_hour', intensity: '60' },
    global: { bloomGlow: 35, saturationIntensity: 55, sharpness: 55 },
  },
  {
    pattern: /\b(أكشن|action|حرب|war|قتال|fight|معركة|battle|explosion|انفجار)\b/i,
    camera: { shotType: 'wide', perspective: 'low_angle', lens: '24mm', dof: 'deep', cameraMovement: 'tracking' },
    light: { lightStyle: 'dramatic', intensity: '70' },
    global: { contrastCurve: 70, dustParticles: 50, motionBlurGlobal: 30 },
  },
  {
    pattern: /\b(هادي|calm|peaceful|سلام|serene|طبيعة|nature|حديقة|garden|بحر|sea|ocean)\b/i,
    camera: { shotType: 'wide', perspective: 'eye_level', lens: '35mm', dof: 'moderate' },
    light: { lightStyle: 'natural', lightPreset: 'natural_daylight', intensity: '65' },
    global: { saturationIntensity: 55, sharpness: 60 },
  },
  {
    pattern: /\b(سايبربانك|cyberpunk|نيون|neon|مستقبل|future|futuristic|sci.?fi|خيال علمي)\b/i,
    camera: { shotType: 'medium', perspective: 'low_angle', lens: '35mm', dof: 'shallow' },
    light: { lightStyle: 'neon', lightPreset: 'cyberpunk', intensity: '60' },
    global: { bloomGlow: 45, chromaticAberration: 25, saturationIntensity: 65, contrastCurve: 65 },
  },
  {
    pattern: /\b(حزين|sad|melancholic|وحيد|lonely|كئيب|gloomy|depressed)\b/i,
    camera: { shotType: 'medium', perspective: 'high_angle', lens: '50mm', dof: 'shallow' },
    light: { lightStyle: 'natural', lightPreset: 'overcast_soft', intensity: '40' },
    global: { saturationIntensity: 30, contrastCurve: 40, fogHaze: 25 },
  },
  {
    pattern: /\b(غامض|mysterious|mystery|سر|secret|ضبابي|misty|foggy)\b/i,
    camera: { shotType: 'medium', perspective: 'eye_level', lens: '50mm', dof: 'moderate' },
    light: { lightStyle: 'volumetric', lightPreset: 'moody_noir', intensity: '45' },
    global: { fogHaze: 60, cinematicSmoke: 35, contrastCurve: 60, saturationIntensity: 35 },
  },
  {
    pattern: /\b(ليل|night|بالليل|nocturnal|midnight|نص الليل)\b/i,
    camera: { shotType: 'medium', lens: '50mm', dof: 'shallow' },
    light: { lightStyle: 'moonlight', lightPreset: 'blue_hour', intensity: '35' },
    global: { contrastCurve: 65, saturationIntensity: 35, shadowBalance: 30 },
  },
  {
    pattern: /\b(مرح|playful|فرح|happy|بهجة|joyful|طفل|child|kids|أطفال)\b/i,
    camera: { shotType: 'medium', perspective: 'eye_level', lens: '35mm', dof: 'moderate' },
    light: { lightStyle: 'natural', lightPreset: 'natural_daylight', intensity: '70' },
    global: { saturationIntensity: 65, sharpness: 60, bloomGlow: 20 },
  },
  {
    pattern: /\b(ملحمي|epic|عظيم|grand|ضخم|massive|legendary)\b/i,
    camera: { shotType: 'extreme_wide', perspective: 'low_angle', lens: '24mm', dof: 'deep' },
    light: { lightStyle: 'volumetric', lightPreset: 'sunset_drama', intensity: '75' },
    global: { contrastCurve: 70, godRaysGlobal: 45, atmosphericDensity: 40 },
  },
];

/**
 * Parse user text and return a settings patch.
 * Explicit mentions always win. If no explicit settings found, infer from mood.
 */
export function parseTextToSettings(text: string): SettingsPatch {
  const clean = text.trim();
  if (!clean) return {};

  const patch: SettingsPatch = {};
  let hasExplicitCamera = false;
  let hasExplicitLight = false;
  let hasExplicitGlobal = false;

  // 1. Aspect Ratio
  for (const rule of ASPECT_RULES) {
    if (rule.pattern.test(clean)) {
      patch.aspectRatio = rule.value;
      break;
    }
  }

  // 2. Camera (explicit)
  const cameraUpdates: Record<string, string> = {};
  for (const rule of CAMERA_RULES) {
    if (rule.pattern.test(clean)) {
      cameraUpdates[rule.key] = rule.value;
      hasExplicitCamera = true;
    }
  }
  if (hasExplicitCamera) patch.camera = cameraUpdates;

  // 3. Light (explicit)
  const lightUpdates: Record<string, string> = {};
  for (const rule of LIGHT_RULES) {
    if (rule.pattern.test(clean)) {
      lightUpdates[rule.key] = rule.value;
      hasExplicitLight = true;
    }
  }
  if (hasExplicitLight) patch.light = lightUpdates;

  // 4. Global (explicit)
  const globalUpdates: Record<string, string | number | boolean> = {};
  for (const rule of GLOBAL_RULES) {
    if (rule.pattern.test(clean)) {
      globalUpdates[rule.key] = rule.value;
      hasExplicitGlobal = true;
    }
  }
  if (hasExplicitGlobal) patch.global = globalUpdates;

  // 5. If no explicit settings at all, infer from mood
  if (!hasExplicitCamera && !hasExplicitLight && !hasExplicitGlobal && !patch.aspectRatio) {
    for (const mood of MOOD_PROFILES) {
      if (mood.pattern.test(clean)) {
        patch.camera = { ...(patch.camera || {}), ...mood.camera };
        patch.light = { ...(patch.light || {}), ...mood.light };
        patch.global = { ...(patch.global || {}), ...mood.global };
        break; // first matching mood wins
      }
    }
  }

  return patch;
}


export function parseDescriptionSettings(description: string): SettingsPatch {
  const clean = description.trim();
  if (!clean) return {};

  const patch = parseTextToSettings(clean);

  if (/\b(cinematic lighting|إضاءة سينمائية|اضاءة سينمائية)\b/i.test(clean)) {
    patch.light = { ...(patch.light || {}), lightStyle: 'dramatic' };
  }

  if (/\b(neon lighting|إضاءة نيون|اضاءة نيون)\b/i.test(clean)) {
    patch.light = { ...(patch.light || {}), lightStyle: 'neon' };
  }

  return patch;
}

/**
 * Check if a patch has any actual changes
 */
export function hasPatchChanges(patch: SettingsPatch): boolean {
  return !!(
    patch.aspectRatio ||
    (patch.camera && Object.keys(patch.camera).length) ||
    (patch.light && Object.keys(patch.light).length) ||
    (patch.global && Object.keys(patch.global).length) ||
    (patch.character && Object.keys(patch.character).length)
  );
}
